import { ErrorBoundary } from '@/components/error-boundary/ErrorBoundary';
import { SplitPane } from '@/components/react-split-pane/components/split-pane/SplitPane';
import { useCalcNotificationHeight } from '@/hooks/useCalcNotificationHeight';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type { HttpApiServerType } from '@/services/http-api/httpApi';
import { ApiType } from '@/shared/constant/ApiType';
import { DebugPanelSize } from '@/shared/constant/ScreenSize';
import AsyncApiRunner from '@/ui-kit/components/ApiRunner/AsyncApiRunner';
import AsyncEnvironmentSelectWrapper from '@/ui-kit/components/ApiRunner/components/EnvironmentSelect/AsyncEnvironmentSelectWrapper';
import { useLocation } from '@remix-run/react';
import { ModelType } from '@tarslib/types/lib/common';
import { Button } from 'aui';
import { FoxIconXClose } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import styles from './DebugPanelGroup.module.css';

export function DebugPanelGroup() {
  const isDebugPanelOpen = useAppearanceConfigModelStore((x) => x.isOpenDebugPanel);
  const setIsOpenDebugPanel = useAppearanceConfigModelStore((x) => x.setIsOpenDebugPanel);
  const setDebugPanelSize = useAppearanceConfigModelStore((x) => x.setDebugPanelSize);
  const notificationHeight = useCalcNotificationHeight();
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const isApiType = useDocsDataModelStore((x) => x.resourceData.type === ModelType.HttpApi);
  const location = useLocation();
  const projectId = useDocsDataModelStore((x) => x.docsIdTypeData.projectId);
  const httpApi = useDocsDataModelStore((x) => x.resourceData.data);
  const serverId = useDocsDataModelStore((x) =>
    x.resourceData.type === ModelType.HttpApi ? x.resourceData.extraData.serverId : undefined,
  );

  const memoHttpApi = React.useMemo(() => {
    if (!httpApi) {
      return undefined;
    }
    return {
      ...httpApi,
      serverId,
    };
  }, [httpApi, serverId]);

  const handleClose = () => {
    setIsOpenDebugPanel(false);
  };

  React.useEffect(() => {
    if (!!isApiType && location.pathname.toLowerCase().endsWith('-run')) {
      setIsOpenDebugPanel(true);
    }
  }, [isApiType, location.pathname, setIsOpenDebugPanel]);
  React.useEffect(() => {
    if (!isApiType) {
      setIsOpenDebugPanel(false);
    }
  }, [isApiType, setIsOpenDebugPanel]);

  if (!isApiType || !isDebugPanelOpen) {
    return null;
  }

  return (
    <SplitPane
      className={clsx('pointer-events-none fixed z-40 w-full py-2.5', {
        ['mx-[max(calc((100vw-1560px)/2),0px)] max-w-[1560px]']: !isDebugPanelOpen,
        ['mx-[max(calc((100vw-1800px)/2),0px)] max-w-[1800px]']: isDebugPanelOpen,
      })}
      style={{
        top: notificationHeight,
        height: `calc(100vh - ${notificationHeight}px)`,
      }}
      primary="second"
      defaultSize={DebugPanelSize.defaultSize}
      minSize={DebugPanelSize.minSize}
      maxSize={DebugPanelSize.maxSize}
      onChange={(size) => {
        setDebugPanelSize(size);
      }}
      resizerClassName={clsx('pointer-events-none hidden', {
        ['os:pointer-events-auto os:flex']: isDebugPanelOpen,
      })}
      pane1ClassName="pointer-events-none"
    >
      <div />
      <div
        className={clsx(
          'pointer-events-auto fixed left-0 top-0 h-full w-full as:relative as:left-auto as:top-auto',
          {
            ['flex']: isDebugPanelOpen,
            ['hidden']: !isDebugPanelOpen,
          },
        )}
      >
        <div
          className="bg-black/70 absolute left-0 top-0 h-full w-full as:hidden"
          onClick={() => {
            setIsOpenDebugPanel(false);
          }}
        />

        <div className="absolute bottom-0 h-[calc(100%-100px)] w-full pl-0 as:relative as:bottom-auto as:h-auto as:w-full as:pr-2.5">
          <div className="h-full w-full overflow-y-hidden rounded-t-xl bg-background shadow-lg as:rounded-xl as:border as:border-border-color-base">
            <header className={clsx(styles.header, 'flex min-h-0 items-center justify-between')}>
              <div className={clsx(styles.headerLeft, 'flex items-center justify-center')}>
                <Button
                  className="mr-1"
                  size="small"
                  type="text"
                  onClick={handleClose}
                  icon={<FoxIconXClose className={clsx(styles.closeIcon)} />}
                />
                <span className={styles.headerTitle}>{clientI18n('runner.tryItOut')}</span>
              </div>
              {memoHttpApi && 'type' in memoHttpApi && memoHttpApi.type !== ApiType.Webhook && (
                <AsyncEnvironmentSelectWrapper />
              )}
            </header>
            {memoHttpApi && (
              <ErrorBoundary>
                <AsyncApiRunner
                  httpApi={memoHttpApi as unknown as HttpApiServerType}
                  projectId={projectId}
                  runStyle={{ height: `calc(100% - 52px)` }}
                />
              </ErrorBoundary>
            )}
          </div>
        </div>
      </div>
    </SplitPane>
  );
}
